import { ref } from 'vue';
import { AxiosError } from 'axios';
import { userApi } from '@/entities/User';
import type { UpdateUserData } from '@/entities/User';
import { useCustomToast } from '@/shared/lib';
import type { CreateUserFormData } from '../ui/CreateUserModal.vue';

export interface ValidationFieldError {
  property: string;
  message: string;
}

export interface CreateUserResult {
  success: boolean;
  fieldErrors?: ValidationFieldError[];
  generatedPassword?: string;
}

export interface UpdateUserResult {
  success: boolean;
  fieldErrors?: ValidationFieldError[];
}

function extractFieldErrors(error: unknown): ValidationFieldError[] | undefined {
  if (!(error instanceof AxiosError)) return undefined;
  if (error.response?.status !== 400) return undefined;

  const data = error.response.data as { errors?: Record<string, string[]> };
  if (!data.errors) return undefined;

  const fieldErrors: ValidationFieldError[] = [];
  for (const [property, messages] of Object.entries(data.errors)) {
    if (messages && messages.length > 0) {
      fieldErrors.push({ property, message: messages[0] });
    }
  }

  return fieldErrors.length > 0 ? fieldErrors : undefined;
}

export function useUserManagement() {
  const toast = useCustomToast();
  const isCreating = ref(false);
  const isUpdating = ref(false);
  const isDeleting = ref(false);
  const isResetting = ref(false);

  async function createUser(data: CreateUserFormData): Promise<CreateUserResult> {
    isCreating.value = true;

    try {
      const result = await userApi.createUserWithMember({
        name: data.name,
        username: data.username,
        email: data.email,
        positions: data.positions,
        registrationNumber: data.registrationNumber,
        admissionDate: data.admissionDate,
        dismissalDate: data.dismissalDate,
        status: data.status,
        role: data.role,
        functionalActivityIds: data.functionalActivityIds,
      });

      toast.showSuccessToast({
        title: 'Usuário criado com sucesso',
        type: 'success',
      });

      return { success: true, generatedPassword: result.generatedPassword };
    } catch (err) {
      const fieldErrors = extractFieldErrors(err);

      if (fieldErrors && fieldErrors.length > 0) {
        toast.showErrorToast({
          title: 'Erro ao criar usuário',
          message: 'Verifique os campos destacados e tente novamente',
          type: 'error',
        });

        return { success: false, fieldErrors };
      }

      toast.showErrorToast({
        title: 'Erro ao criar usuário',
        message: 'Tente novamente',
        type: 'error',
      });

      return { success: false };
    } finally {
      isCreating.value = false;
    }
  }

  async function updateUser(userId: number, data: UpdateUserData): Promise<UpdateUserResult> {
    isUpdating.value = true;

    try {
      await userApi.updateUser(userId, data);

      toast.showSuccessToast({
        title: 'Usuário atualizado com sucesso',
        type: 'success',
      });

      return { success: true };
    } catch (err) {
      const fieldErrors = extractFieldErrors(err);

      if (fieldErrors && fieldErrors.length > 0) {
        toast.showErrorToast({
          title: 'Erro ao atualizar usuário',
          message: 'Verifique os campos destacados e tente novamente',
          type: 'error',
        });

        return { success: false, fieldErrors };
      }

      toast.showErrorToast({
        title: 'Erro ao atualizar usuário',
        message: 'Tente novamente',
        type: 'error',
      });

      return { success: false };
    } finally {
      isUpdating.value = false;
    }
  }

  async function deleteUser(userId: number): Promise<{ success: boolean }> {
    isDeleting.value = true;

    try {
      await userApi.deleteUser(userId);

      toast.showSuccessToast({
        title: 'Usuário deletado com sucesso',
        type: 'success',
      });

      return { success: true };
    } catch {
      toast.showErrorToast({
        title: 'Erro ao deletar usuário',
        message: 'Por favor, tente novamente.',
        type: 'error',
      });

      return { success: false };
    } finally {
      isDeleting.value = false;
    }
  }

  async function resetPassword(userId: number): Promise<{ success: boolean; generatedPassword?: string }> {
    isResetting.value = true;

    try {
      const result = await userApi.resetPassword(userId);

      toast.showSuccessToast({
        title: 'Senha resetada com sucesso',
        type: 'success',
      });

      return { success: true, generatedPassword: result.generatedPassword };
    } catch {
      toast.showErrorToast({
        title: 'Erro ao resetar senha',
        message: 'Por favor, tente novamente.',
        type: 'error',
      });

      return { success: false };
    } finally {
      isResetting.value = false;
    }
  }

  async function getMultipleDeleteDescription(_userIds: number[]): Promise<string> {
    return 'A ação não pode ser desfeita.';
  }

  return {
    isCreating,
    isUpdating,
    isDeleting,
    isResetting,
    createUser,
    updateUser,
    deleteUser,
    resetPassword,
    getMultipleDeleteDescription,
  };
}
